import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from "react-toastify"
import SectionHeader from '../../../common/SectionHeader'
import useFormState from '../../../../hooks/useFormState'
import './style.sass'
import ClientsForm from '../../../common/SharedForms/ClientsForm'
import { getAdminCommentsByApplicationId, getReleaseById, submitReviewForRelease } from '../../../../redux/actions/release'
import { getProductPropertyFormats } from '../../../../redux/actions/lookupData'
import LoadingBlock from '../../../common/LoadingBlock'
import ClientTypesConstants from '../../../../constants/ClientTypes'
import MandatoryScopes from '../../../../constants/MandatoryScopes'
import FloatingBottomActionsBar from '../../../common/FloatingBottomActionsBar'
import CenteredModal from '../../../common/CenteredModal'
import { TextArea } from '../../../common/FormFields'
import FormValidationMessages from '../../../../constants/FormValidationMessages'
import CheckIcon from '../../../../assets/icons/check-icon.svg'
import ErrorIcon from '../../../../assets/icons/exclamation-icon.svg'
import ApiStatusCode from '../../../../constants/ApiStatusCode'
import ReleaseStatus from '../../../../constants/ReleaseStatus'
import {
  VersionInfoForm,
  EnvironmentInfoForm,
  BasicSettingsForm,
  ApplicationAccessForm,
  ApplicationPropertiesForm
} from '../../../common/SharedForms'
import MessageCard from '../../../common/MessageCard'

const PendingReviewDetail = () => {

  // ===== Hooks =====
  const { releaseId } = useParams()
  const history = useHistory()
  // Form states
  const versionInfoFormState = useFormState()
  const environmentInfoFormState = useFormState()
  const basicSettingsFormState = useFormState()
  const appAccessFormState = useFormState()
  const appPropertiesFormState = useFormState()
  const webClientsFormState = useFormState()
  const serverClientsFormState = useFormState()
  const spaClientsFormState = useFormState()
  const mobileClientsFormState = useFormState()

  const { setFormData: setFormDataVersionInfo } = versionInfoFormState
  const { setFormData: setFormDataEnvironmentInfo } = environmentInfoFormState
  const { setFormData: setFormDataBasicSettings } = basicSettingsFormState
  const { setFormData: setFormDataAppAccess } = appAccessFormState
  const { setFormData: setFormDataAppProperties } = appPropertiesFormState
  const { setFormData: setFormDataWebClient } = webClientsFormState
  const { setFormData: setFormDataServerClient } = serverClientsFormState
  const { setFormData: setFormDataSpaClient } = spaClientsFormState
  const { setFormData: setFormDataMobileClient } = mobileClientsFormState

  // ===== Local state =====
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [reviewComment, setReviewComment] = useState({
    value: null,
    error: null
  })

  // ===== Redux state =====
  const dispatch = useDispatch()
  const release = useSelector(({ release }) => release.release)
  const isSubmittingReview = useSelector(({ release }) => release.isSubmittingReview)
  const isFetchingCurrentRelease = useSelector(({ release }) => release.isFetchingCurrentRelease)
  const fetchingAdminComments = useSelector(({ release }) => release.fetchingAdminComments)
  const adminComments = useSelector(({ release }) => release.adminComments)

  // ===== Side effects =====
  useEffect(() => {
    if (releaseId) {
      dispatch(getReleaseById(releaseId))
    }
  }, [releaseId])

  useEffect(() => {
    // Initializing form data
    if ((!isFetchingCurrentRelease) && release?.id?.toString() === releaseId) {
      initializeFormData()
    }
  }, [isFetchingCurrentRelease, release?.updatedDate])

  useEffect(() => {
    // Initializing application property formats
    const app = release?.data ? JSON.parse(release.data) : null
    const categoryName = app?.applicationCategory?.name

    if (categoryName) {
      dispatch(getProductPropertyFormats(categoryName))
    }
  }, [release?.data])

  useEffect(() => {
    if (release?.globalApplicationId && release?.id?.toString() === releaseId) {
      dispatch(getAdminCommentsByApplicationId(release?.globalApplicationId))
    }
  }, [release?.globalApplicationId])

  // ===== Methods =====
  const initializeFormData = () => {
    // Parsing release.data to get the application object
    const app = getApplication()
    // Prevent null pointers
    if (!app) return

    // VersionInfo
    setFormDataVersionInfo({
      releaseId: release.id,
      commentByVendor: release.commentByVendor?.commentText,
      commentByAdmin: release.commentByAdmin?.commentText
    })

    // EnvironmentInfo
    setFormDataEnvironmentInfo({
      requestPlatformCodes: release.requestPlatformCodes
        ?.split(',')
        ?.map(x => ({ value: x })) || []
    })

    // BasicSettings
    setFormDataBasicSettings({
      name: app.name,
      description: app.description,
      applicationCategoryId: app.applicationCategoryId,
      isSingleTenant: app.isSingleTenant,
      image: app.logoImage?.path ? `${process.env.REACT_APP_DEV_PORTAL_API_BASE_URL}/${app.logoImage?.path}` : null,
      agencyCompanyKey: app.agencyCompanyKey
    })
    // AppAccess
    setFormDataAppAccess({
      applicationScopes: app.applicationScopes?.filter(s => s.value !== MandatoryScopes.APPFRAMEWORK)
    })
    // AppProperties
    const appPropertiesObj = {}
    app.applicationProperties?.forEach(p => appPropertiesObj[p.key] = p.value)
    setFormDataAppProperties(appPropertiesObj)

    // Clients
    const webClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.WEB)
    const serverClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.SERVER)
    const spaClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.SPA)
    const mobileClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.MOBILE)
    setFormDataWebClient(webClientObj)
    setFormDataServerClient(serverClientObj)
    setFormDataSpaClient(spaClientObj)
    setFormDataMobileClient(mobileClientObj)
  }

  const onReviewSubmitted = async (approve) => {
    if (!reviewComment.value) {
      // Set error
      setReviewComment(rc => ({ ...rc, error: FormValidationMessages.REQUIRED }))
      return
    }
    // Clear error
    setReviewComment(rc => ({ ...rc, error: null }))

    // Dispactch action and Navigate back
    const res = await dispatch(submitReviewForRelease(releaseId, approve, reviewComment.value))

    if (res?.status === ApiStatusCode.SUCCESS) {
      setReviewComment({})
      history.push('/admin/pending-reviews')
      toast.success('Your review was successfully submitted!')
    }
  }

  const getApplication = () => {
    // Parsing release.data to get the application object
    let app = null
    try {
      app = release?.data ? JSON.parse(release?.data) : null
    } catch (e) {
      /* Fail Silently */
    }
    return app
  }

  const getStatusString = (status) => {
    switch (status) {
      case ReleaseStatus.PENDING:
        return "In review"
      case ReleaseStatus.LIVE:
        return "Live"
      case ReleaseStatus.EXPIRED:
        return "Expired"
      case ReleaseStatus.CANCELED:
        return "Canceled"
      default:
        return null
    }
  }

  const renderAdminComments = () => {
    if (
      fetchingAdminComments ||
      !adminComments ||
      adminComments?.length === 0 ||
      !release?.id?.toString() === releaseId
    ) return null
    return (
      <MessageCard
        title="Previous Comments"
        icon="info-circle"
        type="info"
      >
        <ul>
          {adminComments?.map(item => (
            <li key={item.version}>
              v{item.version?.toFixed(1)} - {item.commentByAdmin.commentText}
            </li>
          ))}
        </ul>
      </MessageCard>
    )
  }

  const renderFloatingActionBar = () => {
    if (release?.status !== ReleaseStatus.PENDING) {
      return
    }

    return (
      <FloatingBottomActionsBar
        mainText={`Approve or Reject this application (v${release?.version?.toFixed(1)} - ${getApplication()?.name}).`}
        // Primary action
        primaryButtonText="Approve"
        onPrimaryButtonPressed={() => setIsApproveModalOpen(true)}
        isProccessingPrimaryAction={isSubmittingReview}
        primaryButtonClass="btn-success-outline"
        // Secondary action
        secondaryButtonText="Reject"
        onSecondaryButtonPressed={() => setIsRejectModalOpen(true)}
        isProccessingSecondaryAction={isSubmittingReview}
        secondaryButtonClass="btn-error-outline"
      />
    )
  }

  const renderApproveModal = () => {
    if (release?.status !== ReleaseStatus.PENDING) {
      return
    }

    return (
      <CenteredModal
        show={isApproveModalOpen}
        title="Approve this application?"
        titlePrefixIcon={CheckIcon}
        successBtnText="Yes, approve!"
        onSuccess={() => onReviewSubmitted(true)}
        cancelBtnText="Cancel"
        onHide={() => {
          setIsApproveModalOpen(false)
          setReviewComment({})
        }}
        btnDisabled={isSubmittingReview}
      >
        <TextArea
          displayName="Comment"
          placeholder="Comment"
          description="This comment will be shown to the developer"
          value={reviewComment.value}
          onChange={value => setReviewComment(rc => ({ ...rc, value }))}
          errorMessage={reviewComment.error}
        />
      </CenteredModal>
    )
  }

  const renderRejectModal = () => {
    if (release?.status !== ReleaseStatus.PENDING) {
      return
    }

    return (
      <CenteredModal
        show={isRejectModalOpen}
        title="Reject this application?"
        titlePrefixIcon={ErrorIcon}
        successBtnText="Yes, reject!"
        onSuccess={() => onReviewSubmitted(false)}
        cancelBtnText="Cancel"
        onHide={() => {
          setIsRejectModalOpen(false)
          setReviewComment({})
        }}
        btnDisabled={isSubmittingReview}
      >
        <TextArea
          displayName="Comment"
          placeholder="Comment"
          description="This comment will be shown to the developer"
          value={reviewComment.value}
          onChange={value => setReviewComment(rc => ({ ...rc, value }))}
          errorMessage={reviewComment.error}
        />
      </CenteredModal>
    )
  }

  if (isFetchingCurrentRelease || !release || release?.id?.toString() !== releaseId) {
    return <LoadingBlock message="Loading..." />
  }

  return (
    <div className="release-details-page-container">
      <SectionHeader
        title={`v${release?.version?.toFixed(1)} - ${getApplication()?.name}`}
        backButtonText="Pending Reviews"
        backButtonOnClick={() => { history.push('/admin/pending-reviews') }}
        status={getStatusString(release?.status)}
      />
      {renderAdminComments()}
      <VersionInfoForm
        formState={versionInfoFormState}
        globalApplicationId={release?.globalApplicationId}
        disabled={true}
      />
      <EnvironmentInfoForm
        formState={environmentInfoFormState}
        disabled={true}
      />
      <BasicSettingsForm
        formState={basicSettingsFormState}
        disabled={true}
      />
      <ApplicationAccessForm
        formState={appAccessFormState}
        appId={release?.applicationId}
        disabled={true}
      />
      <ClientsForm
        formStateWeb={webClientsFormState}
        formStateServer={serverClientsFormState}
        formStateSpa={spaClientsFormState}
        formStateMobile={mobileClientsFormState}
        appStatus={release?.applicationId}
        appScopes={appAccessFormState?.formData?.applicationScopes || []}
        releaseId={release?.id}
        releaseStatus={release?.status}
        disabled={true}
      />
      <ApplicationPropertiesForm
        formState={appPropertiesFormState}
        disabled={true}
      />
      <div className="spacer" />

      {renderFloatingActionBar()}
      {renderApproveModal()}
      {renderRejectModal()}
    </div>
  )
}

export default PendingReviewDetail



